/**
 * Integration hook for synchronizing with open trades
 * PERFORMANCE OPTIMIZED - Uses direct store access to prevent re-renders
 * NOTE: Watchlist initialization is done in AppInitializerContent.tsx
 */

import { useEffect, useRef } from "react";

/**
 * Hook to automatically sync subscribed symbols with open trades
 * This should be used in the main app provider or a high-level component
 */
export const useSubscribedSymbolsSync = () => {
  const hasInitialized = useRef(false);

  // ONE-TIME INITIALIZATION ONLY
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // NOTE: Watchlist initialization is handled in AppInitializerContent.tsx

    // Set up automatic subscription when connection is ready
    const checkConnectionAndSubscribe = () => {
      import(
        "../../connection-progress/stores/useConnectionProgressStore"
      ).then(({ useConnectionProgressStore }) => {
        import("../stores/useSubscribedSymbolsStore").then(
          ({ useSubscribedSymbolsStore }) => {
            const connectionState = useConnectionProgressStore.getState();
            const subscribedState = useSubscribedSymbolsStore.getState();

            if (
              connectionState.isConnected &&
              subscribedState.allSubscribed.length > 0
            ) {
              // Subscribe to all symbols
              import("../../connection/client/VybeTraderClient").then(
                ({ VybeTraderClient }) => {
                  const client = VybeTraderClient.getInstance();
                  subscribedState.allSubscribed.forEach(async (entry) => {
                    try {
                      await client.subscribeToSymbol(entry.symbol.currency);
                    } catch (error) {
                      console.error(
                        `[SubscribedSymbolsSync] Failed to subscribe to ${entry.symbol.currency}:`,
                        error
                      );
                    }
                  });
                }
              );
            } else if (!connectionState.isConnected) {
              // Check again in 100ms if not connected
              setTimeout(checkConnectionAndSubscribe, 100);
            }
          }
        );
      });
    };

    // Start checking immediately — the check itself already polls every 100ms
    // until the connection is up, so a fixed head-start only delays the first
    // quote subscriptions after boot.
    checkConnectionAndSubscribe();
  }, []); // Empty dependency array - run once only

  // Return static values to avoid re-renders
  return {
    isReady: true, // Always ready to avoid hook dependencies
    tradesCount: 0, // Don't expose dynamic values
    uniqueSymbolsInTrades: 0, // Don't expose dynamic values
  };
};
